import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useShop } from '../../../shop/ShopProvider';
import {
  addDays,
  describeRange,
  endOfMonth,
  isIsoDate,
  PRESETS,
  startOfMonth,
  type DateRange,
  type Preset,
} from '../lib/dateRange';

/** Range picker: preset list plus a custom from/to, shown as one compact button. */
export function DateRangeControl({
  range,
  preset,
  onPreset,
  onCustom,
}: {
  range: DateRange;
  preset: Preset;
  onPreset: (p: Exclude<Preset, 'custom'>) => void;
  onCustom: (r: DateRange) => void;
}) {
  const { format } = useShop();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DateRange>(range);
  const presetLabel = PRESETS.find((p) => p.value === preset)?.label ?? 'Custom';
  const valid = isIsoDate(draft.from) && isIsoDate(draft.to) && draft.from <= draft.to;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={(e) => {
          setDraft(range);
          setAnchor(e.currentTarget);
        }}
        startIcon={<CalendarMonthRounded sx={{ color: 'primary.main' }} />}
        endIcon={<ExpandMoreRounded />}
        aria-label="Date range"
        sx={{
          borderColor: 'divider',
          bgcolor: 'background.paper',
          justifyContent: 'flex-start',
          minWidth: 0,
          maxWidth: '100%',
          '& .label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        }}
      >
        <Box component="span" className="label" sx={{ textAlign: 'left' }}>
          <Box component="span" sx={{ fontWeight: 650 }}>
            {presetLabel}
          </Box>
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 1 }}>
            {describeRange(range, format.locale)}
          </Box>
        </Box>
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: '14px', width: { xs: 320, sm: 520 } } } }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          <List dense sx={{ p: 1, minWidth: 180 }} aria-label="Presets">
            {PRESETS.map((p) => (
              <ListItemButton
                key={p.value}
                selected={p.value === preset}
                onClick={() => {
                  onPreset(p.value);
                  setAnchor(null);
                }}
                sx={{ borderRadius: '8px', mb: 0.25 }}
              >
                <ListItemText
                  primary={p.label}
                  slotProps={{ primary: { sx: { fontWeight: 600, fontSize: '0.875rem' } } }}
                />
                {p.value === preset && <CheckRounded fontSize="small" color="primary" />}
              </ListItemButton>
            ))}
          </List>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Divider sx={{ display: { sm: 'none' } }} />
          <Box sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Custom range
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                type="date"
                label="From"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="date"
                label="To"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                error={isIsoDate(draft.from) && isIsoDate(draft.to) && draft.from > draft.to}
                helperText={
                  isIsoDate(draft.from) && isIsoDate(draft.to) && draft.from > draft.to
                    ? 'End date is before the start date'
                    : ' '
                }
              />
              <Button
                variant="contained"
                disabled={!valid}
                onClick={() => {
                  onCustom(draft);
                  setAnchor(null);
                }}
              >
                Apply range
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
}

/** "As of" date for balance reports, with quick picks. */
export function AsOfControl({
  value,
  today,
  onChange,
}: {
  value: string;
  today: string;
  onChange: (iso: string) => void;
}) {
  const lastMonthEnd = addDays(startOfMonth(today), -1);
  const quick = [
    { label: 'Today', value: today },
    { label: 'End of last month', value: lastMonthEnd },
    { label: 'End of last year', value: `${String(Number(today.slice(0, 4)) - 1)}-12-31` },
  ];
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      <TextField
        type="date"
        label="As of"
        value={value}
        onChange={(e) => {
          if (isIsoDate(e.target.value)) onChange(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: endOfMonth(today) } }}
        sx={{ width: 170, bgcolor: 'background.paper', borderRadius: '10px' }}
      />
      {quick.map((q) => (
        <Button
          key={q.label}
          size="small"
          variant={q.value === value ? 'contained' : 'text'}
          color={q.value === value ? 'primary' : 'inherit'}
          onClick={() => onChange(q.value)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {q.label}
        </Button>
      ))}
    </Stack>
  );
}
